import { DROPDOWN_FETCH_PAGE_SIZE, useCurrentUser, usePermissions } from '@klareospace/pharos-lib-front-utils';
import { USER_STATUS, type Task } from '@klareospace/pharos-lib-types';

import { useCarriersForSelect } from '&/features/settings/carriers/hooks';
import { useDrivers } from '&/features/settings/drivers/hooks';

import {
  buildCarrierOptions,
  buildDriverOptions,
  isAssignmentEditable,
  NONE_CARRIER_VALUE,
  resolveDriverFieldDisabled,
  resolveDriverFieldEditable,
  resolveMode,
  TASK_ASSIGNMENT_MODE,
  type AssignmentSelectOption,
  type TaskAssignmentMode,
} from '../utils/task-assignment';
import { useTaskAssignmentDraft, type UseTaskAssignmentDraftReturn } from './use-task-assignment-draft';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseTaskCarrierDriverFieldsReturn extends UseTaskAssignmentDraftReturn {
  /** Selectable carrier options for the current mode (see `buildCarrierOptions`). */
  carrierOptions: AssignmentSelectOption[];
  /** Whether the Driver select should be disabled (see `resolveDriverFieldDisabled`). */
  driverFieldDisabled: boolean;
  /** Selectable driver options for the current mode (see `buildDriverOptions`). */
  driverOptions: AssignmentSelectOption[];
  /** Whether the Carrier field renders as an editable select rather than read-only text. */
  isCarrierEditable: boolean;
  /** Whether the Driver field renders as an editable select rather than read-only text. */
  isDriverEditable: boolean;
  /** Resolved mode (see `resolveMode`); forced to `readOnly` outside the status-editable window. */
  mode: TaskAssignmentMode;
  /** Whether the "No driver" option should appear in the driver select (assign mode, or self-release with `tasks:release`). */
  showDriverNoneOption: boolean;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/** Resolves the assignment mode, field editability, and select options; composes `useTaskAssignmentDraft`. */
export function useTaskCarrierDriverFields(
  task: Task,
  onTaskUpdated: (task: Task) => void,
): UseTaskCarrierDriverFieldsReturn {
  const { hasPermission } = usePermissions();
  const { user } = useCurrentUser();

  const permissionMode = resolveMode(hasPermission('tasks:assign'), hasPermission('tasks:self-assign'), user);
  // Status lock overrides permissions — always read-only outside the editable window.
  const mode = isAssignmentEditable(task.status) ? permissionMode : TASK_ASSIGNMENT_MODE.READ_ONLY;

  const draft = useTaskAssignmentDraft(task, mode, onTaskUpdated);

  const fetchedCarrierOptions = useCarriersForSelect({
    enabled: mode === TASK_ASSIGNMENT_MODE.ASSIGN,
    enabledOnSiteId: task.site.id,
  }).map((opt) => ({ id: opt.value, name: opt.label }));
  const carrierOptions = buildCarrierOptions(mode, fetchedCarrierOptions, user, task);

  const isCarrierAssigned = draft.draftCarrierId !== NONE_CARRIER_VALUE;
  const { data: driversData } = useDrivers({
    carrierId: isCarrierAssigned ? draft.draftCarrierId : undefined,
    enabled: mode === TASK_ASSIGNMENT_MODE.ASSIGN && isCarrierAssigned,
    page: 1,
    pageSize: DROPDOWN_FETCH_PAGE_SIZE,
    statuses: [USER_STATUS.ACTIVE],
  });
  const driverOptions = buildDriverOptions(mode, driversData?.items, user, draft.draftCarrierId);

  const isCarrierEditable = mode !== TASK_ASSIGNMENT_MODE.READ_ONLY;
  const isDriverEditable = resolveDriverFieldEditable(mode, task, user);
  const driverFieldDisabled = resolveDriverFieldDisabled(mode, draft.isPending, draft.draftCarrierId, user);

  const isSelfAssignedDriver = mode === TASK_ASSIGNMENT_MODE.SELF_ASSIGN && task.driver?.id === user?.id;
  const showDriverNoneOption =
    mode === TASK_ASSIGNMENT_MODE.ASSIGN || (isSelfAssignedDriver && hasPermission('tasks:release'));

  return {
    ...draft,
    carrierOptions,
    driverFieldDisabled,
    driverOptions,
    isCarrierEditable,
    isDriverEditable,
    mode,
    showDriverNoneOption,
  };
}
